import { errorToast } from "@/v2-ui/toast"
import Manage from "@/v2-backoffice-client/components/manage"
import { useUserQuery } from "@/v2-backoffice-client/user/user.query"
import type { TenantFragment } from "@/v2-backoffice-client/tenant/__types__/tenant.fragment"
import type { UserFragment } from "@/v2-backoffice-client/user/__types__/user.fragment"
import TenantUserManageSearchItem from "@/v2-backoffice-client/tenant/user/manage/TenantUserManageSearchItem"

const MIN_SEARCH_LENGTH = 2

export type TenantUserManageSearchProps = {
  open: boolean
  tenantId: number
  /** The team's customer link, for the cross-customer guard on each hit. */
  tenant?: Pick<TenantFragment, "ExternalCustomerId" | "CustomerName"> | null
  debouncedSearch: string
  isMember(userId: number): boolean
  onAdded(user: UserFragment, roleCode: number): void
}

function TenantUserManageSearch(props: TenantUserManageSearchProps) {
  const { open, tenantId, tenant, debouncedSearch, isMember, onAdded } = props

  const hasQuery = debouncedSearch.length >= MIN_SEARCH_LENGTH

  const { data, loading, error } = useUserQuery({
    skip: !open || !hasQuery,
    variables: {
      input: {
        limit: 10,
        filters: hasQuery
          ? [{ column: "Email", contains: debouncedSearch }]
          : undefined,
      },
    },
  })

  errorToast(error, { title: "Failed to search users" })

  const users = data?.getUsers.items ?? []
  const results = users.filter((user) => !isMember(user.Id))

  return (
    <Manage.Section
      label={`Users not yet in the team${results.length > 0 ? ` (${results.length})` : ""}`}
      className="border-t border-border"
    >
      {!hasQuery && (
        <Manage.Hint>
          Type at least {MIN_SEARCH_LENGTH} characters to search
        </Manage.Hint>
      )}

      {hasQuery && loading && <Manage.Skeleton count={3} />}
      {hasQuery && !loading && results.length === 0 && (
        <Manage.Empty>
          No users found that aren&apos;t already in the team
        </Manage.Empty>
      )}

      {hasQuery && !loading && results.length > 0 && (
        <Manage.List variant="results">
          {results.map((user) => (
            <TenantUserManageSearchItem
              key={user.Id}
              user={user}
              tenantId={tenantId}
              tenant={tenant}
              onAdded={(roleCode) => onAdded(user, roleCode)}
            />
          ))}
        </Manage.List>
      )}
    </Manage.Section>
  )
}

export default TenantUserManageSearch
